/**
 * Shared types + pure helpers for the standalone Notes module.
 *
 * Notes are plain-text `type='note'` objects (see docs/design/notes-conventions.md).
 * The display title is always DERIVED from the first non-empty line of the body —
 * there is no separate title field in the editor — and the stored `title` is kept
 * in sync with that derivation so other surfaces (chat cards, search) have a label.
 */
import type { XaviorObjectBase } from '@/features/objects/hooks';

/** A note as returned by the objects API (attributes flattened to top-level). */
export interface NoteObject extends XaviorObjectBase {
  title?: string | null;
  body?: string | null;
  summary?: string | null;
  pinned?: boolean;
  archivedAt?: string | null;
  contentHash?: string | null;
  googleDriveFileId?: string | null;
  googleDriveSyncedAt?: string | null;
  moduleIds?: string[];
  status?: string;
}

/** Max characters of the first line used as a derived title. */
export const MAX_DERIVED_TITLE = 80;

/** Fallback stored title for a body with no text yet. */
export const UNTITLED_TITLE = 'Untitled note';

/** First non-empty, trimmed line of a body (or '' if none). */
export function firstNonEmptyLine(body: string | null | undefined): string {
  if (!body) return '';
  for (const raw of body.split('\n')) {
    const line = raw.trim();
    if (line) return line;
  }
  return '';
}

/** Title derived from the body's first line, capped at MAX_DERIVED_TITLE. */
export function deriveTitle(body: string | null | undefined): string {
  return firstNonEmptyLine(body).slice(0, MAX_DERIVED_TITLE);
}

/** The title to persist alongside a body (never empty, so the API never falls back to body-as-title). */
export function titleForPersist(body: string | null | undefined): string {
  return deriveTitle(body) || UNTITLED_TITLE;
}

export interface DisplayTitle {
  text: string;
  /** true when the body is empty — the UI renders this italic/faded. */
  untitled: boolean;
}

/** Display title for a note card / editor (derived from body; italic "Untitled note" when empty). */
export function displayTitle(note: { body?: string | null }): DisplayTitle {
  const derived = deriveTitle(note.body);
  if (derived) return { text: derived, untitled: false };
  return { text: UNTITLED_TITLE, untitled: true };
}

/** Two-line snippet: everything after the first non-empty line, collapsed to a single string. */
export function snippetOf(body: string | null | undefined): string {
  const lines = (body || '').split('\n').map((s) => s.trim()).filter(Boolean);
  return lines.slice(1).join(' ');
}

/** A note is archived when it carries an archivedAt timestamp. */
export function isArchived(note: NoteObject): boolean {
  return Boolean(note.archivedAt);
}

/**
 * A permanent delete of an archived note 404s, because archived objects are
 * excluded from the ACL scope the API's DELETE handler checks. The fix is to
 * restore the note first (which brings it back into scope) and then delete it.
 * Returns true when the delete flow must restore before deleting.
 */
export function needsRestoreBeforeDelete(note: NoteObject): boolean {
  return isArchived(note);
}

/**
 * Sentinel `activeId` for the "new draft" state: an empty, editable editor that
 * is NOT yet backed by a persisted object. No note is created until the user's
 * first real keystroke (`shouldCreateFromDraft`).
 */
export const NEW_NOTE_SENTINEL = '__new__';

/**
 * Whether typing into the pending-new editor should create the note now. Only
 * the first non-whitespace input triggers a create; whitespace-only input never
 * persists an empty note.
 */
export function shouldCreateFromDraft(draft: string): boolean {
  return draft.trim().length > 0;
}

/**
 * Reverse-chronological by updatedAt. Stable for equal timestamps.
 */
export function byUpdatedDesc(a: NoteObject, b: NoteObject): number {
  const ta = Date.parse(a.updatedAt || '') || 0;
  const tb = Date.parse(b.updatedAt || '') || 0;
  return tb - ta;
}
